import React from 'react';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import useWindowSize from '@hooks/use-window-size';
import { useGetProductDrafts } from '@hooks/product-listing/product-listing-drafts-hooks';
import useUser from '@hooks/user/use-user';
import logger from '@helpers/utils/logger';
import { AI_PRODUCT_DRAFT_ID_PARAM } from '@pages/ai-listing/utils/query-params';
import clsx from 'clsx';
import AiListingApi from '@pages/ai-listing/services/ai-listing-api';
import { useAiListingTracking } from '@hooks/ai-listing/use-ai-listing-tracking';
import AuthenticationGuard from '@components/atoms/authentication/authentication-guard/authentication-guard';
import Icon from '@components/atoms/icon/icon';
import Spinner from '@components/atoms/spinner/spinner';
import FieldError from '../listing-form/fields/field-error/field-error';
import styles from './listing-options-button.module.scss';

interface ListingOptionsButtonProps {
  sellButtonLink: string;
  openModal?: boolean;
  isBottomNavigation?: boolean;
  isOnSellPage?: boolean;
  buttonLabel?: string;
  buttonSize?: 'small' | 'medium';
  /** `fullWidth` stretches the toggle button to fill its container (e.g. confirmation footer). */
  variant?: 'default' | 'fullWidth';
}

const ListingOptionsButton: React.FC<ListingOptionsButtonProps> = (props) => {
  const {
    sellButtonLink,
    openModal,
    isBottomNavigation,
    isOnSellPage,
    buttonLabel,
    buttonSize = 'small',
    variant = 'default',
  } = props; // Destructure any props you need here
  const { t } = useTranslation('default-layout');
  const router = useRouter();
  const { isWiderThanLg } = useWindowSize();
  const { isAuthenticated } = useUser();
  const { trackListingStart, trackSellAnItem, trackViewDrafts } = useAiListingTracking();
  const [isModalOpen, setIsModalOpen] = React.useState(openModal ?? false);
  const [isLoading, setIsLoading] = React.useState(false);
  const [isError, setIsError] = React.useState<boolean>(false);
  const scrollPositionRef = React.useRef<number>(0);
  const modalContentRef = React.useRef<HTMLDivElement>(null);
  const toggleButtonRef = React.useRef<HTMLButtonElement>(null);

  const { data } = useGetProductDrafts({
    enabled: isAuthenticated && isModalOpen,
    limit: 1,
  });
  const existingDraftCount = data?.pages?.[0].nbPreducts || 0;

  // Handle body scroll and overflow when modal is open
  React.useEffect(() => {
    if (!isWiderThanLg) {
      if (isModalOpen) {
        // Scroll to 5rem (80px) to account for smart banner pushing header down
        window.scrollTo(0, 80);
        // Prevent body scroll
        document.body.style.overflow = 'hidden';
      } else {
        // Restore body scroll when modal closes
        document.body.style.overflow = '';
        // Restore scroll position when modal closes on mobile
        if (scrollPositionRef.current > 0) {
          window.scrollTo(0, scrollPositionRef.current);
        }
      }
    }
    // Cleanup on unmount
    return () => {
      document.body.style.overflow = '';
    };
  }, [isModalOpen, isWiderThanLg]);

  // Auto-open the popin from a CRM campaign link (e.g. /?openSellOptions=true)
  React.useEffect(() => {
    if (router.query.openSellOptions === 'true') {
      setIsModalOpen(true);
    }
  }, [router.query.openSellOptions]);

  // Handle click outside modal to close it
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      if (
        isModalOpen &&
        modalContentRef.current &&
        toggleButtonRef.current &&
        !modalContentRef.current.contains(event.target as Node) &&
        !toggleButtonRef.current.contains(event.target as Node)
      ) {
        setIsModalOpen(false);
        setIsError(false);
      }
    };

    if (isModalOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isModalOpen]);

  const infoList = [
    {
      title: t('AI_LISTING.OPTIONS_BUTTON.INFOS.LIST.TITLE', 'List'),
      subtitle: t(
        'AI_LISTING.OPTIONS_BUTTON.INFOS.LIST.SUBTITLE',
        "Add photos and details. We'll recommend the right price.",
      ),
    },
    {
      title: t('AI_LISTING.OPTIONS_BUTTON.INFOS.SELL.TITLE', 'Sell'),
      subtitle: t(
        'AI_LISTING.OPTIONS_BUTTON.INFOS.SELL.SUBTITLE',
        'Share with millions of shoppers and negotiate directly to sell faster.',
      ),
    },
    {
      title: t('AI_LISTING.OPTIONS_BUTTON.INFOS.SHIP.TITLE', 'Ship'),
      subtitle: t(
        'AI_LISTING.OPTIONS_BUTTON.INFOS.SHIP.SUBTITLE',
        'Drop off your item with a free, insured prepaid label.',
      ),
    },
    {
      title: t('AI_LISTING.OPTIONS_BUTTON.INFOS.GET_PAID.TITLE', 'Get Paid'),
      subtitle: t(
        'AI_LISTING.OPTIONS_BUTTON.INFOS.GET_PAID.SUBTITLE',
        'Enjoy quick payout for more of your sale price.',
      ),
    },
  ];

  const toggleModal = () => {
    if (!isModalOpen) {
      // Fire only when this click opens the listing-start popup (not on the close toggle).
      trackSellAnItem();
      // Capture scroll position before opening modal on mobile
      if (!isWiderThanLg) {
        scrollPositionRef.current = window.scrollY || window.pageYOffset;
      }
    }
    setIsModalOpen(!isModalOpen);
  };

  const handleAiListingClick = async () => {
    trackListingStart('ai_listing');
    if (isAuthenticated) {
      try {
        setIsError(false);
        setIsLoading(true);
        const response = await AiListingApi.createAiProductDraft();
        const aiProductDraftId = response.data.id;
        await router.push(`/ai-listing/?${AI_PRODUCT_DRAFT_ID_PARAM}=${aiProductDraftId}`);
      } catch (error) {
        logger.error('ai-listing::createAiProductDraft::', error);
        setIsError(true);
        setIsLoading(false);
      }
    }
  };

  return (
    <div
      className={clsx(
        styles.listingOptionsButton,
        variant === 'fullWidth' && styles['listingOptionsButton--fullWidth'],
      )}
    >
      <button
        ref={toggleButtonRef}
        type="button"
        data-cy="sell_an_item"
        data-vc-dd-action-name="sell_an_item"
        className={clsx(
          styles.listingOptionsButton__btn,
          !isBottomNavigation && `vc-btn vc-btn--primary vc-btn--${buttonSize}`,
          isBottomNavigation && styles['listingOptionsButton__btn--bottomNav'],
          variant === 'fullWidth' && styles['listingOptionsButton__btn--fullWidth'],
        )}
        onClick={toggleModal}
      >
        {isBottomNavigation ? (
          <>
            <Icon
              className={styles.bottomNavigation__icon}
              name={isOnSellPage ? 'sell-full' : 'sell-line'}
              ariaHidden
            />
            <span
              className={clsx(
                styles.bottomNavigation__title,
                isOnSellPage && styles['bottomNavigation__title--active'],
              )}
              aria-hidden="true"
            >
              {t('HEADER.NAVIGATION.SELL_TITLE')}
            </span>
          </>
        ) : (
          buttonLabel ?? t('HEADER.SELL_AN_ITEM_BUTTON')
        )}
      </button>
      <div
        className={clsx(styles.listingOptionsButton__modal, {
          [styles['listingOptionsButton__modal--open']]: isModalOpen,
        })}
      >
        <div
          className={styles.listingOptionsButton__modal__content}
          ref={modalContentRef}
        >
          {isBottomNavigation && (
            <div className={styles.listingOptionsButton__modal__header}>
              <p className={styles.listingOptionsButton__modal__header__title}>
                {t('AI_LISTING.OPTIONS_BUTTON.INFOS.TITLE', 'Sell an item')}
              </p>
              <p className={styles.listingOptionsButton__modal__header__subtitle}>
                {t('AI_LISTING.START_PAGE.SUBTITLE', 'List in minutes. Ship for free. Start earning.')}
              </p>
            </div>
          )}
          <ul className={styles.listingOptionsButton__modal__options}>
            <li
              className={clsx(
                styles.listingOptionsButton__modal__options__item,
                styles['listingOptionsButton__modal__options__item--ai'],
              )}
            >
              <div className={styles.listingOptionsButton__modal__options__item__title}>
                <Icon
                  name="ai-icon-default"
                  className={styles.listingOptionsButton__modal__options__item__icon}
                  ariaHidden
                />
                {t('AI_LISTING.OPTIONS_BUTTON.SMART_LISTING.TITLE', 'Smart listing')}
                <span className={clsx('vc-chip vc-chip--ai', styles.listingOptionsButton__modal__options__item__beta)}>
                  {t('AI_LISTING.OPTIONS_BUTTON.SMART_LISTING.BETA', 'BETA')}
                </span>
              </div>
              <div className={styles.listingOptionsButton__modal__options__item__subtitle}>
                {t('AI_LISTING.OPTIONS_BUTTON.SMART_LISTING.SUBTITLE', 'Scan photos to help you list faster.')}
              </div>
              <div className={styles.listingOptionsButton__modal__options__item__buttonWrapper}>
                <AuthenticationGuard shouldPreventDefault={true}>
                  <button
                    type="button"
                    data-cy="sell_an_item"
                    data-vc-dd-action-name="sell_an_item"
                    className={clsx(
                      styles.listingOptionsButton__modal__options__item__link,
                      'vc-btn vc-btn--rounded vc-btn--medium',
                      isLoading && styles['listingOptionsButton__modal__options__item__link--loading'],
                      isLoading && 'vc-btn--with-icon',
                    )}
                    onClick={handleAiListingClick}
                    disabled={isLoading}
                  >
                    <span className={styles.listingOptionsButton__modal__options__item__linkText}>
                      {t('AI_LISTING.OPTIONS_BUTTON.SMART_LISTING.LINK', 'Use smart listing')}
                    </span>
                  </button>
                </AuthenticationGuard>
                {isLoading && <Spinner className={styles.listingOptionsButton__modal__options__item__spinner} />}
              </div>
              {isError && (
                <FieldError className={styles.listingOptionsButton__modal__options__item__error}>
                  {t('AI_LISTING.ERROR_CREATE_DRAFT', 'Something went wrong, please try again.')}
                </FieldError>
              )}
            </li>
            <li className={styles.listingOptionsButton__modal__options__item}>
              <div className={styles.listingOptionsButton__modal__options__item__title}>
                {t('AI_LISTING.OPTIONS_BUTTON.MANUAL_LISTING.TITLE', 'Manual listing')}
              </div>
              <div className={styles.listingOptionsButton__modal__options__item__subtitle}>
                {t('AI_LISTING.OPTIONS_BUTTON.MANUAL_LISTING.SUBTITLE', 'Fill the information by yourself.')}
              </div>
              <a
                href={sellButtonLink}
                data-cy="sell_an_item"
                data-vc-dd-action-name="sell_an_item"
                className={clsx(
                  styles.listingOptionsButton__modal__options__item__link,
                  'vc-btn vc-btn--rounded-secondary vc-btn--medium',
                )}
                onClick={() => trackListingStart('manual_listing')}
              >
                {t('AI_LISTING.OPTIONS_BUTTON.MANUAL_LISTING.LINK', 'Use manual listing')}
              </a>
              {existingDraftCount > 0 && (
                <div className={styles.listingOptionsButton__modal__options__item__draft}>
                  <span>
                    {existingDraftCount === 1
                      ? t(
                          'AI_LISTING.OPTIONS_BUTTON.MANUAL_LISTING.DRAFTS.TEXT_SINGULAR',
                          'You have 1 draft in progress',
                        )
                      : t(
                          'AI_LISTING.OPTIONS_BUTTON.MANUAL_LISTING.DRAFTS.TEXT',
                          'You have {{numberDrafts}} drafts in progress',
                          {
                            numberDrafts: existingDraftCount,
                          },
                        )}
                  </span>
                  <a
                    href="/sell-clothes-online/product-drafts/"
                    onClick={() => trackViewDrafts()}
                  >
                    {t('AI_LISTING.OPTIONS_BUTTON.MANUAL_LISTING.DRAFTS.LINK', 'View drafts')}
                  </a>
                </div>
              )}
            </li>
          </ul>

          {isBottomNavigation && (
            <div className={styles.listingOptionsButton__modal__infos}>
              <p className={styles.listingOptionsButton__modal__infos__title}>
                {t('AI_LISTING.OPTIONS_BUTTON.INFOS.TITLE', 'How it works')}
              </p>
              <ul className={styles.listingOptionsButton__modal__infos__list}>
                {infoList.map((info) => (
                  <li
                    key={info.title}
                    className={styles.listingOptionsButton__modal__infos__list__item}
                  >
                    <Icon
                      name="arrow-right-long-default"
                      className={styles.listingOptionsButton__modal__infos__list__icon}
                      ariaHidden
                    />
                    <div>
                      <p className={styles.listingOptionsButton__modal__infos__list__item__title}>{info.title}</p>
                      <p className={styles.listingOptionsButton__modal__infos__list__item__subtitle}>{info.subtitle}</p>
                    </div>
                  </li>
                ))}
              </ul>
              <div className={styles.listingOptionsButton__modal__infos__footerLinks}>
                <a
                  className={styles.listingOptionsButton__modal__infos__link}
                  href="/journal/start-selling-now/"
                >
                  {t('AI_LISTING.OPTIONS_BUTTON.INFOS.LINK', 'Learn more')}
                </a>
                <p className={styles.listingOptionsButton__modal__infos__note}>
                  {t('AI_LISTING.OPTIONS_BUTTON.INFOS.NOTE', '*Payment terms and T&C apply.')}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ListingOptionsButton;
